import React, { useState } from "react";
import styles from "../Reservations/styles/View.module.scss";
import { Col, Row, Card, CardBody } from "reactstrap";
import { convertDate, getDate, getDateTime, getReservationTypeLabel } from "../../helpers/functions";
import { Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { AiOutlineComment, AiOutlineEdit } from "react-icons/ai";
import PayoutCommentModal from "./components/PayoutCommentModal";

export default function PayoutView({ payoutDetail, onUpdated }) {
    const navigate = useNavigate();
    const [commentModalOpen, setCommentModalOpen] = useState(false);

    const getFacility = (status) => {
        if (status === 0) return "Non Facility";
        else if (status === 1) return "Facility";
        else return "";
    };

    const getPaymentStatusLabel = (status) => {
        switch (status) {
            case '0':
                return 'Pending';
            case '1':
                return 'Paid';
            case '2':
                return 'Refund';
            case '3':
                return 'Partial Refund';
            case '4':
                return 'Dispute';
            default:
                return 'Unknown';
        }
    };

    if (!payoutDetail) {
        return <div>Loading...</div>;
    }

    const reservation = payoutDetail.Reservation;
    const space = reservation?.Space;
    const renter = reservation?.users;
    const host = space?.User || payoutDetail.User;

    return (
        <Row>
            <Col md="12">
                <Card className="main-card mb-3">
                    <CardBody>
                        <Row className="mb-3">
                            <Col md="12" className="text-right d-flex justify-content-between align-items-center">
                                <h3>Payout Details</h3>
                                <Button color="secondary" onClick={() => navigate(-1)}>
                                    Back
                                </Button>
                            </Col>
                        </Row>

                        <div className={styles.section}>
                            <h6 className={styles.sectionTitle}>Payout Summary</h6>
                            <Row>
                                <Col md="3">
                                    <h6 className={styles.header}>Transaction Code</h6>
                                    <p className={styles.subHeader}>{payoutDetail?.transactionCode}</p>
                                </Col>
                                <Col md="3">
                                    <h6 className={styles.header}>Payment Status</h6>
                                    <p className={styles.subHeader}>{getPaymentStatusLabel(payoutDetail?.paymentStatus)}</p>
                                </Col>
                                <Col md="3">
                                    <h6 className={styles.header}>Payment Date</h6>
                                    <p className={styles.subHeader}>{convertDate(new Date(payoutDetail?.paymentDate))}</p>
                                </Col>
                                <Col md="3">
                                    <h6 className={styles.header}>Currency</h6>
                                    <p className={styles.subHeader}>{payoutDetail?.currencyCode}</p>
                                </Col>
                            </Row>
                        </div>

                        <div className={styles.section}>
                            <div className={styles.commentBox}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className={styles.commentLabel}>
                                        <AiOutlineComment /> Payout Comment
                                    </h6>
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={() => setCommentModalOpen(true)}
                                    >
                                        <AiOutlineEdit /> {payoutDetail?.payoutComment ? 'Edit' : 'Add'}
                                    </Button>
                                </div>
                                {payoutDetail?.payoutComment ? (
                                    <p className={styles.commentText}>{payoutDetail.payoutComment}</p>
                                ) : (
                                    <p className={styles.commentEmpty}>No comment added yet.</p>
                                )}
                            </div>
                        </div>

                        {reservation && (
                            <div className={styles.section}>
                                <h6 className={styles.sectionTitle}>Reservation</h6>
                                <Row>
                                    <Col md="3">
                                        <h6 className={styles.header}>Reservation Code</h6>
                                        <p className={styles.subHeader}>{reservation?.reservationCode || 'N/A'}</p>
                                    </Col>
                                    <Col md="3">
                                        <h6 className={styles.header}>Reservation Type</h6>
                                        <p className={styles.subHeader}>{getReservationTypeLabel(reservation?.reservationType)}</p>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        <div className={styles.section}>
                            <h6 className={styles.sectionTitle}>People &amp; Space</h6>
                            <Row>
                                <Col md="3">
                                    <h6 className={styles.header}>Host</h6>
                                    {host ? (
                                        <>
                                            <p className={styles.subHeader}>{host.id}</p>
                                            <p className={styles.subHeader}>{`${host.firstName} ${host.lastName}`}</p>
                                            <p className={styles.subHeader}>{host.email}</p>
                                            <p className={styles.subHeader}>{host.phoneNumber}</p>
                                        </>
                                    ) : (
                                        <p className={styles.subHeader}>N/A</p>
                                    )}
                                </Col>
                                <Col md="3">
                                    <h6 className={styles.header}>Renter</h6>
                                    {renter ? (
                                        <>
                                            <p className={styles.subHeader}>{renter.id}</p>
                                            <p className={styles.subHeader}>{`${renter.firstName} ${renter.lastName}`}</p>
                                            <p className={styles.subHeader}>{renter.email}</p>
                                            <p className={styles.subHeader}>{renter.phoneNumber}</p>
                                        </>
                                    ) : (
                                        <p className={styles.subHeader}>N/A</p>
                                    )}
                                </Col>
                                <Col md="3">
                                    <h6 className={styles.header}>Space</h6>
                                    {space ? (
                                        <>
                                            <p className={styles.subHeader}>{space.id}</p>
                                            <p className={styles.subHeader}>{space.title}</p>
                                            <p className={styles.subHeader}>
                                                {space.addressOne && space.addressOne}
                                                {space.addressTwo && ", " + space.addressTwo} <br />
                                                {space.city && space.city}
                                                {space.stateCode && ", " + space.stateCode}
                                                {space.zipCode && ", " + space.zipCode}
                                            </p>
                                            <p className={styles.subHeader}>
                                                {getFacility(space.facility)}
                                            </p>
                                        </>
                                    ) : (
                                        <p className={styles.subHeader}>N/A</p>
                                    )}
                                </Col>
                            </Row>
                        </div>

                        <div className={styles.section}>
                            <h6 className={styles.sectionTitle}>Financial Breakdown</h6>
                            <Row>
                                <Col md="12">
                                    <p className={styles.subHeader}> Rate: {payoutDetail?.rate} </p>
                                    <p className={styles.subHeader}> Gross Amount: {payoutDetail?.grossAmount} </p>
                                    <p className={styles.subHeader}> Net Amount: {payoutDetail?.netAmount} </p>
                                    <p className={styles.subHeader}> Discount Amount: {payoutDetail?.discountAmount} </p>
                                    <p className={styles.subHeader}> Host Service Fee %: {payoutDetail?.hostServiceFeePercent} </p>
                                    <p className={styles.subHeader}> Service Fee: {payoutDetail?.serviceFee} </p>
                                    <p className={styles.subHeader}> Fixed Fee: {payoutDetail?.fixedFee} </p>
                                    <p className={styles.subHeader}> Sales Tax: {payoutDetail?.salesTax} </p>
                                    <hr />
                                    <h6 className={styles.header}><strong>Final Payout Amount: {payoutDetail?.amount}</strong></h6>
                                </Col>
                            </Row>
                        </div>
                    </CardBody>
                </Card>
            </Col>
            <PayoutCommentModal
                isModalOpen={commentModalOpen}
                toggleModal={() => setCommentModalOpen(false)}
                selectedPayout={payoutDetail}
                onUpdated={onUpdated}
            />
        </Row>
    );
}
